/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { toast } from 'react-toastify';
import { getApi, postApi } from 'views/services/api';

const cellKey = (roomTypeId, stayTypeId) => `${roomTypeId}_${stayTypeId}`;

export default function StepRates({ hotel, hotelId, onNext, onBack }) {
  const [roomTypes, setRoomTypes] = useState([]);
  const [stayTypes, setStayTypes] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const currency = hotel?.currency || 'USD';

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [typesRes, staysRes, ratesRes] = await Promise.all([
          getApi(`api/room/room-types/list/${hotelId}`),
          getApi(`api/hotel/stay-types/list/${hotelId}`),
          getApi(`api/room/rates/matrix/${hotelId}`)
        ]);

        if (typesRes?.status === 200) setRoomTypes(typesRes.data || []);
        if (staysRes?.status === 200) setStayTypes(staysRes.data || []);
        if (ratesRes?.status === 200 && Array.isArray(ratesRes.data)) {
          const prefill = {};
          ratesRes.data.forEach((r) => {
            prefill[cellKey(r.roomTypeId, r.stayTypeId)] = r.amount;
          });
          setMatrix(prefill);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [hotelId]);

  const updateCell = (roomTypeId, stayTypeId, value) => {
    setMatrix((prev) => ({ ...prev, [cellKey(roomTypeId, stayTypeId)]: value }));
  };

  const handleNext = async () => {
    // Requerimos al menos el precio "Por Noche" de cada tipo de habitación
    const nightly = stayTypes.find((s) => s.isNightly);
    if (nightly) {
      const missing = roomTypes.filter((rt) => {
        const v = matrix[cellKey(rt._id, nightly._id)];
        return v === undefined || v === '' || Number(v) <= 0;
      });
      if (missing.length > 0) {
        toast.error(`Falta el precio por noche de: ${missing.map((m) => m.name).join(', ')}`);
        return;
      }
    }

    setSaving(true);
    try {
      for (const rt of roomTypes) {
        for (const st of stayTypes) {
          const raw = matrix[cellKey(rt._id, st._id)];
          if (raw === undefined || raw === '') continue;
          const amount = Number(raw);
          if (Number.isNaN(amount) || amount < 0) continue;

          const res = await postApi('api/room/rates/upsert', {
            hotelId,
            roomTypeId: rt._id,
            stayTypeId: st._id,
            amount
          });
          if (res?.status !== 200) {
            toast.error(`No se pudo guardar el precio de ${rt.name} / ${st.label}`);
            setSaving(false);
            return;
          }
        }
      }

      // Propagar precios a las habitaciones creadas
      const syncRes = await postApi('api/room/rates/sync', { hotelId });
      if (syncRes?.status !== 200) {
        toast.error('Los precios se guardaron pero no se pudieron sincronizar con las habitaciones');
        setSaving(false);
        return;
      }

      toast.success('Precios guardados y aplicados a tus habitaciones');
      onNext();
    } catch (e) {
      console.error(e);
      toast.error('Error guardando los precios');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (roomTypes.length === 0 || stayTypes.length === 0) {
    return (
      <Stack spacing={3}>
        <Alert severity="warning">
          Necesitas al menos un tipo de habitación y un tipo de estancia para configurar precios. Vuelve a los pasos anteriores.
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ alignSelf: 'flex-start', textTransform: 'none' }}>
          Atrás
        </Button>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" gutterBottom>
          Precios por tipo de habitación y estancia
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Ingresa el precio de cada combinación en {currency}. El precio por noche es obligatorio; los demás son opcionales.
        </Typography>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Tipo de habitación</TableCell>
              {stayTypes.map((st) => (
                <TableCell key={st._id} align="center" sx={{ fontWeight: 700 }}>
                  {st.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {roomTypes.map((rt) => (
              <TableRow key={rt._id}>
                <TableCell sx={{ fontWeight: 600 }}>{rt.name}</TableCell>
                {stayTypes.map((st) => (
                  <TableCell key={st._id} align="center" sx={{ minWidth: 120 }}>
                    <TextField
                      size="small"
                      type="number"
                      inputProps={{ min: 0, step: '0.01' }}
                      value={matrix[cellKey(rt._id, st._id)] ?? ''}
                      onChange={(e) => updateCell(rt._id, st._id, e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">{currency === 'USD' ? '$' : ''}</InputAdornment>
                      }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider />

      <Stack direction="row" justifyContent="space-between">
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ textTransform: 'none' }} disabled={saving}>
          Atrás
        </Button>
        <Button variant="contained" onClick={handleNext} disabled={saving} sx={{ px: 4, fontWeight: 600 }}>
          {saving ? 'Guardando...' : 'Guardar precios y continuar'}
        </Button>
      </Stack>
    </Stack>
  );
}
